import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Calendar, IndianRupee, Sparkles, Target, Clock, Eye, AlertCircle, List, X, Filter, Search } from 'lucide-react';
import apiService from '../services/api';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';
const ProjectTracker = ({ projects, setProjects, milestones, setMilestones, onNavigate }) => {
  const navigate = useNavigate();

  // Local state for real-time updates
  const [localProjects, setLocalProjects] = useState(projects);
  const [localMilestones, setLocalMilestones] = useState(milestones);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // States
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [hoveredEmpty, setHoveredEmpty] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  // Initial data fetch and setup
  useEffect(() => {
    fetchProjects();
    fetchMilestones();
    fetchEmployees();
    fetchCustomStatuses();
  }, []);

  // Set up global refresh function for cross-component updates
  useEffect(() => {
    console.log('ProjectTracker - Setting up window.refreshProjectTracker');
    window.refreshProjectTracker = () => {
      console.log('ProjectTracker - Global refresh triggered');
      fetchProjects();
      fetchMilestones();
    };

    return () => {
      console.log('ProjectTracker - Cleaning up window.refreshProjectTracker');
      delete window.refreshProjectTracker;
    };
  }, []);

  // Listen for project updates from other components
  useEffect(() => {
    const handleProjectUpdate = () => {
      console.log('ProjectTracker - Received project update event');
      fetchProjects();
    };

    // Listen for custom events
    window.addEventListener('projectsUpdated', handleProjectUpdate);

    return () => {
      window.removeEventListener('projectsUpdated', handleProjectUpdate);
    };
  }, []);

  // Update local state when props change
  useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  useEffect(() => {
    setLocalMilestones(milestones);
  }, [milestones]);

  // Fetch functions (keeping existing logic)
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('ProjectTracker - Fetching projects with token:', !!token);

      if (!token) {
        console.error('ProjectTracker - No access token found');
        setLocalProjects([]);
        return;
      }

      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;
      const userId = decodedToken.id || decodedToken._id;
      console.log('ProjectTracker - User role:', userRole, 'User ID:', userId);

      // Use the same endpoint as ProjectList.jsx
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('ProjectTracker - Projects API response status:', response.status);

      if (response.ok) {
        let data = await response.json();
        console.log('ProjectTracker - Raw projects response:', data);
        // ProjectList.jsx directly uses the data array
        let projectList = Array.isArray(data) ? data : [];
        console.log('ProjectTracker - Processed project list (before filter):', projectList.length);

        // Filter projects based on user role
        if (userRole === 'employee' || userRole === 'team_member') {
          projectList = projectList.filter(project => {
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`ProjectTracker - Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('ProjectTracker - Filtered projects for team member:', projectList.length);
        }

        console.log('ProjectTracker - Sample project:', projectList[0]);
        console.log('ProjectTracker - Total projects found:', projectList.length);
        setLocalProjects(projectList);
      } else {
        console.error('ProjectTracker - Failed to fetch projects:', response.status);
        setLocalProjects([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching projects:', error);
      setLocalProjects([]);
    }
  };

  const fetchMilestones = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/milestones', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let milestones = data.milestones || data;
        console.log('ProjectTracker - Fetched milestones (before filter):', Array.isArray(milestones) ? milestones.length : 0);

        // Filter milestones based on user role
        let decodedToken;
        try {
          decodedToken = jwtDecode(token);
        } catch (e) {
          console.error('ProjectTracker - Error decoding token:', e);
        }

        if (decodedToken) {
          const userRole = decodedToken.role;
          const userId = decodedToken.id || decodedToken._id;
          console.log('ProjectTracker - User role:', userRole, 'User ID:', userId);

          // Normalize role to lowercase for comparison
          const normalizedRole = userRole ? userRole.toLowerCase() : '';

          if (Array.isArray(milestones) && (normalizedRole === 'employee' || normalizedRole === 'team_member')) {
            console.log('ProjectTracker - Applying filter for employee/team_member');
            const originalCount = milestones.length;

            milestones = milestones.filter(milestone => {
              const assignedTeamMember = milestone.assignedTeamMember;

              if (!assignedTeamMember) return false;

              // Handle array of assigned members
              if (Array.isArray(assignedTeamMember)) {
                const isAssigned = assignedTeamMember.some(member => {
                  const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                  return String(memberId) === String(userId);
                });
                if (isAssigned) console.log(`ProjectTracker - Milestone "${milestone.title}" - MATCH FOUND`);
                return isAssigned;
              }

              // Handle single assigned member
              const memberId = typeof assignedTeamMember === 'object' ?
                (assignedTeamMember._id || assignedTeamMember.id) : assignedTeamMember;
              const isAssigned = String(memberId) === String(userId);
              if (isAssigned) console.log(`ProjectTracker - Milestone "${milestone.title}" - MATCH FOUND`);
              return isAssigned;
            });

            console.log(`ProjectTracker - Filtering complete. Kept ${milestones.length} out of ${originalCount} milestones.`);
          }
        }

        setLocalMilestones(Array.isArray(milestones) ? milestones : []);

        // Filter for overdue and upcoming project-tasks (next 7 days)
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        const overdueAndUpcoming = (Array.isArray(milestones) ? milestones : []).filter(milestone => {
          if (!milestone.dueDate) return false;
          const dueDate = new Date(milestone.dueDate);
          // Include overdue (past due) and upcoming (next 7 days)
          return dueDate <= sevenDaysFromNow;
        });

        console.log('ProjectTracker - Overdue and upcoming project-tasks:', overdueAndUpcoming);
        console.log('ProjectTracker - Sample milestone data:', overdueAndUpcoming[0]);
        if (overdueAndUpcoming[0]) {
          console.log('ProjectTracker - Milestone fields:', Object.keys(overdueAndUpcoming[0]));
          console.log('ProjectTracker - Project ID field:', overdueAndUpcoming[0].project || overdueAndUpcoming[0].projectId);
          console.log('ProjectTracker - Assigned member field:', overdueAndUpcoming[0].assignedTeamMember);
        }
      } else {
        console.error('ProjectTracker - Failed to fetch milestones:', response.status);
        setLocalMilestones([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching milestones:', error);
      setLocalMilestones([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const employeeList = data.employees || data;
        console.log('ProjectTracker - Fetched employees:', employeeList);
        setEmployees(Array.isArray(employeeList) ? employeeList : []);
      } else {
        console.error('ProjectTracker - Failed to fetch employees:', response.status);
        setEmployees([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching employees:', error);
      setEmployees([]);
    }
  };

  // Function to fetch custom statuses from API
  const fetchCustomStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/prjCustomStatus/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const statuses = await response.json();
        console.log('ProjectTracker - API Response:', statuses);
        console.log('ProjectTracker - Response type:', typeof statuses);
        console.log('ProjectTracker - Is array:', Array.isArray(statuses));

        // Handle wrapped response format {statuses: Array}
        let statusArray = [];
        if (Array.isArray(statuses)) {
          statusArray = statuses;
        } else if (statuses && statuses.statuses && Array.isArray(statuses.statuses)) {
          console.log('ProjectTracker - Found wrapped statuses array');
          statusArray = statuses.statuses;
        } else {
          console.log('ProjectTracker - Invalid response format:', statuses);
          setStatusOptions([]);
          return;
        }

        if (statusArray.length > 0) {
          console.log('ProjectTracker - First status object:', statusArray[0]);
          console.log('ProjectTracker - First status keys:', Object.keys(statusArray[0]));

          // Extract status names from the array
          const statusNames = statusArray.map(status => {
            console.log('ProjectTracker - Processing status:', status);
            // Try to get the name field
            if (status && typeof status === 'object' && status.name) {
              console.log('ProjectTracker - Found name:', status.name);
              return status.name;
            }
            console.log('ProjectTracker - No name field found');
            return null;
          }).filter(name => name); // Remove null values

          console.log('ProjectTracker - Extracted status names:', statusNames);
          setStatusOptions(statusNames);
        } else {
          console.log('ProjectTracker - Empty status array');
          setStatusOptions([]);
        }
      } else {
        console.error('ProjectTracker - API Error:', response.status);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Fetch Error:', error);
      setStatusOptions([]);
    }
  };

  // Helper functions
  const getEmployeeName = (assignedTeamMember) => {
    console.log('ProjectTracker - Looking for assigned team member:', assignedTeamMember);
    console.log('ProjectTracker - Available employees:', employees.map(emp => ({ id: emp._id, name: emp.name || emp.teamMemberName })));

    if (!assignedTeamMember) {
      return 'Unassigned';
    }

    // Handle case where assignedTeamMember is an object
    if (typeof assignedTeamMember === 'object') {
      const result = assignedTeamMember.teamMemberName || assignedTeamMember.name || 'Unknown';
      console.log('ProjectTracker - Employee name from object:', result);
      return result;
    }

    // Handle case where assignedTeamMember is an ID string
    const employee = employees.find(emp => emp._id === assignedTeamMember);
    const result = employee ? (employee.teamMemberName || employee.name || 'Unknown') : 'Unknown Employee';
    console.log('ProjectTracker - Employee name result:', result);
    return result;
  };

  const getProjectNameForMilestone = (milestone) => {
    console.log('ProjectTracker - Full milestone object:', milestone);

    // First priority: Use projectName directly from milestone (most reliable)
    if (milestone.projectName) {
      console.log('ProjectTracker - Using projectName from milestone:', milestone.projectName);
      return milestone.projectName;
    }

    // Second priority: Try to find project by ID
    const projectId = milestone.projectId || milestone.project;
    console.log('ProjectTracker - Looking for project ID:', projectId);
    console.log('ProjectTracker - Available projects:', localProjects.map(p => ({ id: p._id, name: p.title || p.name })));

    if (projectId && localProjects.length > 0) {
      // Try exact match first
      let project = localProjects.find(p => p._id === projectId);

      // If not found, try string comparison
      if (!project) {
        project = localProjects.find(p => p._id?.toString() === projectId?.toString());
      }

      if (project) {
        const result = project.title || project.name;
        console.log('ProjectTracker - Found project by ID:', result);
        return result;
      }
    }

    // Third priority: Check if project field is an object with name
    if (milestone.project && typeof milestone.project === 'object') {
      const result = milestone.project.title || milestone.project.name;
      if (result) {
        console.log('ProjectTracker - Using project object name:', result);
        return result;
      }
    }

    console.log('ProjectTracker - No project name found, using fallback');
    return 'Unknown Project';
  };

  const handleCardClick = (item, event) => {
    event.stopPropagation();
    console.log('ProjectTracker - Item clicked:', item);
    console.log('ProjectTracker - Item type:', item.type);

    // Only navigate for projects, not for milestones/tasks
    if (item.type === 'project') {
      console.log('ProjectTracker - Navigating to project:', item._id);
      if (onNavigate) {
        onNavigate(`/projects/${item._id}`, 'Project Details');
      } else {
        navigate(`/projects/${item._id}`);
      }
    } else if (item.type === 'milestone') {
      // For milestones/tasks, do nothing to prevent navigation
      // The modal/details view should be handled by a separate button or action
      console.log('ProjectTracker - Milestone clicked, no navigation');
      return;
    }
  };

  // Organize data into columns using dynamic statuses
  const safeProjects = Array.isArray(localProjects) ? localProjects : [];
  const safeMilestones = Array.isArray(localMilestones) ? localMilestones : [];

  // Create dynamic columns based on available statuses
  const getColumns = () => {
    const allStatuses = statusOptions.length > 0 ? statusOptions : ['Planning', 'In Progress', 'Completed'];
    const columns = {};

    allStatuses.forEach((status, index) => {
      const colors = ['#ef4444', '#8b5cf6', '#3b82f6', '#f59e0b', '#10b981', '#8b5cf6'];
      const icons = [Clock, Target, Sparkles, AlertCircle, Eye, Plus];

      columns[status.toLowerCase().replace(/\s+/g, '-')] = {
        title: status,
        items: [
          ...safeProjects.filter(p => p.status === status).map(p => ({ ...p, type: 'project', id: p._id })),
          ...safeMilestones.filter(m => m.status === status).map(m => ({ ...m, type: 'milestone', id: m._id }))
        ],
        color: colors[index % colors.length],
        icon: icons[index % icons.length]
      };
    });

    return columns;
  };

  const columns = getColumns();

  // Styles
  const projectTrackerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    position: 'relative',
    overflow: 'hidden'
  };

  const contentWrapperStyle = {
    position: 'relative',
    zIndex: 1
  };

  const pageHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '2rem',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(25px)',
    borderRadius: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    animation: 'slideInDown 0.8s ease-out'
  };

  const headerIconStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  };

  const headerTitleStyle = {
    fontSize: '2.5rem',
    fontWeight: 900,
    color: '#1f2937',
    margin: 0,
    letterSpacing: '1px'
  };

  const keyframesStyle = `
    @keyframes slideInDown {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes cardPop {
      from { opacity: 0; transform: scale(0.96) translateY(10px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }

    /* ===== MOBILE RESPONSIVE STYLES ===== */
    .pt-wrapper { padding: 1rem; }
    .pt-card-container { display: none; }
    .pt-table-container { display: block; }

    /* Mobile header controls */
    .pt-header { display: flex; justify-content: space-between; align-items: center; padding: 0.875rem 1rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; flex-wrap: wrap; gap: 0.5rem; }
    .pt-header-title { font-size: 1rem; font-weight: 700; color: #1f2937; margin: 0; }
    .pt-controls { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
    .pt-search-box { display: flex; align-items: center; gap: 0.5rem; background: white; padding: 0.4rem 0.75rem; border-radius: 0.5rem; border: 1px solid #d1d5db; min-width: 220px; }
    .pt-filter-box { display: flex; align-items: center; gap: 0.5rem; background: white; padding: 0.4rem 0.75rem; border-radius: 0.5rem; border: 1px solid #d1d5db; }

    /* Mobile card styles */
    .pt-mobile-card {
      background: white;
      border-radius: 0.75rem;
      border: 1px solid #e8eaed;
      border-left: 4px solid #3b82f6;
      box-shadow: 0 2px 10px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02);
      padding: 0.85rem 0.9rem 0.85rem 1rem;
      margin-bottom: 0.65rem;
      cursor: pointer;
      transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
      animation: cardPop 0.35s ease both;
      position: relative;
      overflow: hidden;
    }
    .pt-mobile-card::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 100%;
      background: linear-gradient(135deg, rgba(59,130,246,0.03) 0%, transparent 60%);
      pointer-events: none;
    }
    .pt-mobile-card:active { transform: scale(0.98); box-shadow: 0 1px 4px rgba(0,0,0,0.1); }
    .pt-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem; gap: 0.5rem; position: relative; z-index: 1; }
    .pt-card-name { font-size: 0.88rem; font-weight: 700; color: #111827; line-height: 1.35; flex: 1; }
    .pt-card-sub { font-size: 0.68rem; color: #6b7280; font-weight: 500; display: block; margin-top: 2px; }
    .pt-card-badges { display: flex; gap: 0.4rem; flex-wrap: wrap; }
    .pt-badge { padding: 0.18rem 0.5rem; border-radius: 0.375rem; font-size: 0.65rem; font-weight: 700; white-space: nowrap; letter-spacing: 0.04em; }
    .pt-badge-project { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
    .pt-badge-task { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
    .pt-card-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 0.45rem 0.7rem; margin-top: 0.5rem; position: relative; z-index: 1; }
    .pt-meta-item { display: flex; align-items: center; gap: 0.3rem; font-size: 0.75rem; color: #4b5563; }
    .pt-meta-label { font-size: 0.6rem; color: #9ca3af; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; display: block; margin-bottom: 2px; }
    .pt-meta-val { font-size: 0.78rem; color: #1f2937; font-weight: 600; }
    .pt-card-divider { height: 1px; background: linear-gradient(90deg, #e5e7eb 0%, transparent 100%); margin: 0.5rem 0; position: relative; z-index: 1; }
    .pt-status-pill { display: inline-flex; align-items: center; padding: 0.2rem 0.55rem; border-radius: 0.375rem; font-size: 0.67rem; font-weight: 700; letter-spacing: 0.02em; }

    /* Mobile search & filter row */
    .pt-mobile-controls { display: none; flex-direction: column; gap: 0.5rem; padding: 0.6rem 0.75rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; overflow: visible; max-width: 100%; box-sizing: border-box; }
    .pt-mobile-search { display: flex; align-items: center; gap: 0.4rem; background: white; border: 1px solid #d1d5db; border-radius: 0.5rem; padding: 0.4rem 0.6rem; height: 36px; width: 100%; max-width: 100%; box-sizing: border-box; overflow: hidden; }
    .pt-mobile-search input { flex: 1; min-width: 0; border: none; outline: none; font-size: 0.8rem; color: #1f2937; background: transparent; height: 100%; }

    /* Custom mobile dropdown */
    .pt-dropdown-wrapper { position: relative; width: 100%; box-sizing: border-box; }
    .pt-dropdown-trigger {
      display: flex; align-items: center; gap: 0.4rem; background: white; border: 1px solid #d1d5db;
      border-radius: 0.5rem; padding: 0.4rem 0.6rem; height: 36px; width: 100%; max-width: 100%;
      box-sizing: border-box; cursor: pointer; transition: all 0.2s ease; user-select: none;
    }
    .pt-dropdown-trigger.pt-dd-open { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
    .pt-dropdown-trigger-text { flex: 1; font-size: 0.8rem; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .pt-dropdown-chevron { transition: transform 0.25s ease; color: #6b7280; flex-shrink: 0; }
    .pt-dropdown-chevron.pt-dd-rotated { transform: rotate(180deg); }

    @keyframes ptDropSlide {
      from { opacity: 0; transform: translateY(-6px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .pt-dropdown-menu {
      position: absolute; top: calc(100% + 4px); left: 0; right: 0;
      background: rgba(255,255,255,0.97); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(59,130,246,0.15); border-radius: 0.625rem;
      box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 8px rgba(59,130,246,0.08);
      z-index: 999; overflow: hidden; max-height: 220px; overflow-y: auto;
      animation: ptDropSlide 0.2s cubic-bezier(0.16,1,0.3,1) both;
    }
    .pt-dropdown-item {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.55rem 0.75rem; font-size: 0.8rem; color: #374151;
      cursor: pointer; transition: all 0.15s ease; border-bottom: 1px solid #f3f4f6;
      min-height: 38px; box-sizing: border-box;
    }
    .pt-dropdown-item:last-child { border-bottom: none; }
    .pt-dropdown-item:active { background: #eff6ff; }
    .pt-dropdown-item.pt-dd-active {
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
      color: #1d4ed8; font-weight: 700;
      box-shadow: inset 3px 0 0 #3b82f6;
    }
    .pt-dropdown-item .pt-dd-check { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #d1d5db; flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease; }
    .pt-dropdown-item.pt-dd-active .pt-dd-check { border-color: #3b82f6; background: #3b82f6; }
    .pt-dropdown-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 998; background: transparent; }

    @media (max-width: 768px) {
      .pt-wrapper { padding: 0.5rem; }
      .pt-card-container { display: block !important; }
      .pt-table-container { display: none !important; }
      .pt-controls { display: none !important; }
      .pt-mobile-controls { display: flex; }
      .pt-header { padding: 0.6rem 0.75rem; }
      .pt-header-title { font-size: 0.9rem; }
    }
  `;

  return (
    <>
      <style>{keyframesStyle}</style>
      <div style={projectTrackerStyle}>
        <div style={contentWrapperStyle}>
          <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', animation: 'fadeInUp 1s ease-out 0.3s both' }}>

            {/* Shared Header */}
            <div className="pt-header">
              <h3 className="pt-header-title">Project List View</h3>
              <div className="pt-controls">
                <div className="pt-search-box">
                  <Search size={18} style={{ color: '#6b7280' }} />
                  <input type="text" placeholder="Search projects..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ background: 'transparent', border: 'none', color: '#1f2937', fontSize: '0.85rem', outline: 'none', width: '100%' }} />
                </div>
                <div className="pt-filter-box">
                  <Filter size={18} style={{ color: '#6b7280' }} />
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ background: 'transparent', border: 'none', color: '#1f2937', fontSize: '0.85rem', cursor: 'pointer', outline: 'none' }}>
                    <option value="all">All Status</option>
                    {statusOptions.map(s => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
              </div>
            </div>

            {/* Mobile-only controls */}
            <div className="pt-mobile-controls">
              <div className="pt-mobile-search">
                <Search size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                <input type="text" placeholder="Search projects..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <div className="pt-dropdown-wrapper">
                {mobileDropdownOpen && <div className="pt-dropdown-overlay" onClick={() => setMobileDropdownOpen(false)} />}
                <div className={`pt-dropdown-trigger ${mobileDropdownOpen ? 'pt-dd-open' : ''}`}
                  onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}>
                  <Filter size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <span className="pt-dropdown-trigger-text">
                    {statusFilter === 'all' ? 'All Status' : statusFilter}
                  </span>
                  <svg className={`pt-dropdown-chevron ${mobileDropdownOpen ? 'pt-dd-rotated' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                </div>
                {mobileDropdownOpen && (
                  <div className="pt-dropdown-menu">
                    {[{ value: 'all', label: 'All Status' }, ...statusOptions.map(s => ({ value: s, label: s }))].map(opt => (
                      <div key={opt.value}
                        className={`pt-dropdown-item ${statusFilter === opt.value ? 'pt-dd-active' : ''}`}
                        onClick={() => { setStatusFilter(opt.value); setMobileDropdownOpen(false); }}>
                        <span className="pt-dd-check">
                          {statusFilter === opt.value && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                          )}
                        </span>
                        <span>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Column Headers */}
            <div className="pt-table-container" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: '1rem', padding: '1rem 1.5rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: '0.875rem', color: '#374151' }}>
              <div>Name</div><div>Type</div><div>Status</div><div>Due Date</div><div>Budget</div><div>Team</div>
            </div>

            {/* Content */}
            {(() => {
              const now = new Date();
              const oneDayFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
              const urgentMilestones = safeMilestones.filter(m => {
                if (!m.dueDate) return false;
                const d = new Date(m.dueDate);
                return d > now && d <= oneDayFromNow;
              });
              const allItems = [
                ...safeProjects.map(p => ({ ...p, type: 'project', id: p._id })),
                ...urgentMilestones.map(m => ({ ...m, type: 'milestone', id: m._id }))
              ];
              let filteredItems = allItems;
              if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                filteredItems = filteredItems.filter(item => {
                  const n = (item.name || item.title || '').toLowerCase();
                  const pn = (item.projectName || '').toLowerCase();
                  const d2 = (item.description || '').toLowerCase();
                  return n.includes(q) || pn.includes(q) || d2.includes(q);
                });
              }
              if (statusFilter !== 'all') filteredItems = filteredItems.filter(item => item.status === statusFilter);
              const hasNoData = safeProjects.length === 0 && safeMilestones.length === 0;
              const hasNoResults = filteredItems.length === 0 && (safeProjects.length > 0 || safeMilestones.length > 0);
              const getStatusColor = (status) => {
                const s = (status || '').toLowerCase();
                if (s.includes('planning') || s.includes('todo')) return '#ef4444';
                if (s.includes('progress') || s.includes('working')) return '#8b5cf6';
                if (s.includes('completed') || s.includes('done')) return '#10b981';
                return '#f59e0b';
              };
              if (hasNoData) return (
                <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: '#6b7280' }}>
                  <Sparkles size={42} style={{ opacity: 0.4 }} />
                  <h3 style={{ margin: '0.75rem 0 0.4rem', fontSize: '1.1rem', fontWeight: 600 }}>No tasks yet</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>Projects and project-tasks will appear here when available</p>
                </div>
              );
              if (hasNoResults) return (
                <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: '#6b7280' }}>
                  <Search size={42} style={{ opacity: 0.4 }} />
                  <h3 style={{ margin: '0.75rem 0 0.4rem', fontSize: '1.1rem', fontWeight: 600 }}>No results found</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>{searchQuery ? `No projects found matching "${searchQuery}"` : 'No projects match the selected filters'}</p>
                  {searchQuery && <button onClick={() => setSearchQuery('')} style={{ marginTop: '1rem', padding: '0.45rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>Clear search</button>}
                </div>
              );
              return (
                <>
                  {/* Desktop Table Rows */}
                  <div className="pt-table-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {filteredItems.map((item, index) => {
                      const isProject = item.type === 'project';
                      const statusColor = getStatusColor(item.status);
                      return (
                        <div key={item.id || index} onClick={(e) => handleCardClick(item, e)}
                          onMouseEnter={() => setHoveredCard(item.id)} onMouseLeave={() => setHoveredCard(null)}
                          style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: '1rem', padding: '1rem 1.5rem', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'all 0.2s ease', backgroundColor: hoveredCard === item.id ? '#f9fafb' : 'white', transform: hoveredCard === item.id ? 'translateY(-1px)' : 'translateY(0)', boxShadow: hoveredCard === item.id ? '0 2px 4px rgba(0,0,0,0.1)' : 'none' }}>
                          <div style={{ fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {item.name || item.title}
                            {!isProject && item.projectName && <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>({item.projectName})</span>}
                          </div>
                          <div><span style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: isProject ? '#dbeafe' : '#fed7aa', color: isProject ? '#1e40af' : '#ea580c' }}>{isProject ? 'PROJECT' : 'PROJECT-TASK'}</span></div>
                          <div><span style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: `${statusColor}20`, color: statusColor }}>{item.status || 'Pending'}</span></div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}><Calendar size={14} />{new Date(item.deadline || item.dueDate).toLocaleDateString('en-GB')}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>{isProject ? (<><IndianRupee size={14} />{item.budget?.toLocaleString('en-IN') || 0}</>) : (<span style={{ color: '#9ca3af' }}>-</span>)}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>{isProject ? (<><User size={14} />{item.teamMembers?.length || 0} members</>) : (<><User size={14} /><span style={{ color: '#1f2937', fontWeight: 600 }}>{getEmployeeName(item.assignedTeamMember)}</span></>)}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Mobile Cards */}
                  <div className="pt-card-container" style={{ padding: '0.75rem' }}>
                    {filteredItems.map((item, index) => {
                      const isProject = item.type === 'project';
                      const statusColor = getStatusColor(item.status);
                      const dueStr = (item.deadline || item.dueDate) ? new Date(item.deadline || item.dueDate).toLocaleDateString('en-GB') : '—';
                      return (
                        <div key={item.id || index} className="pt-mobile-card" onClick={(e) => handleCardClick(item, e)} style={{ animationDelay: `${index * 0.05}s`, borderLeftColor: statusColor }}>
                          <div className="pt-card-top">
                            <div style={{ flex: 1 }}>
                              <div className="pt-card-name">{item.name || item.title}</div>
                              {!isProject && item.projectName && <span className="pt-card-sub">{item.projectName}</span>}
                            </div>
                            <span className={`pt-badge ${isProject ? 'pt-badge-project' : 'pt-badge-task'}`}>{isProject ? 'PROJECT' : 'TASK'}</span>
                          </div>
                          <div className="pt-card-divider" />
                          <div className="pt-card-meta">
                            <div>
                              <span className="pt-meta-label">Status</span>
                              <span className="pt-status-pill" style={{ backgroundColor: `${statusColor}18`, color: statusColor }}>{item.status || 'Pending'}</span>
                            </div>
                            <div>
                              <span className="pt-meta-label">Due Date</span>
                              <div className="pt-meta-item"><Calendar size={12} style={{ color: '#6b7280', flexShrink: 0 }} /><span className="pt-meta-val">{dueStr}</span></div>
                            </div>
                            {isProject ? (
                              <>
                                <div>
                                  <span className="pt-meta-label">Budget</span>
                                  <div className="pt-meta-item"><IndianRupee size={12} style={{ color: '#6b7280', flexShrink: 0 }} /><span className="pt-meta-val">{item.budget?.toLocaleString('en-IN') || '0'}</span></div>
                                </div>
                                <div>
                                  <span className="pt-meta-label">Team</span>
                                  <div className="pt-meta-item"><User size={12} style={{ color: '#6b7280', flexShrink: 0 }} /><span className="pt-meta-val">{item.teamMembers?.length || 0} members</span></div>
                                </div>
                              </>
                            ) : (
                              <div style={{ gridColumn: 'span 2' }}>
                                <span className="pt-meta-label">Assigned To</span>
                                <div className="pt-meta-item"><User size={12} style={{ color: '#6b7280', flexShrink: 0 }} /><span className="pt-meta-val">{getEmployeeName(item.assignedTeamMember)}</span></div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectTracker;
